import {
  Calendar,
  CalendarClock,
  Car,
  ClipboardList,
  FileText,
  GraduationCap,
  LayoutDashboard,
  NotebookPen,
  Receipt,
  User,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { WorkspaceReference } from "../../api/references.api";
export const referenceLabels: Record<WorkspaceReference["entityType"], string> =
  {
    CANDIDATE: "Élève",
    INSTRUCTOR: "Moniteur / formateur",
    SECRETARY: "Secrétaire",
    TASK: "Tâche",
    NOTE: "Note",
    MEETING: "Réunion",
    BOARD: "Tableau",
    LESSON: "Leçon",
    EXAM: "Examen",
    VEHICLE: "Véhicule",
    INVOICE: "Facture / avoir",
    PAYMENT: "Paiement",
    DOCUMENT: "Document candidat",
    RDVPERMIS_SLOT: "Créneau RDVPermis",
  };
export const CARD_MIME = "application/x-permio-reference";

export function referenceUrl(ref: WorkspaceReference) {
  return "/workspace/reference?type=" + ref.entityType + "&id=" + ref.entityId;
}
export function parseReferenceUrl(
  value: string,
  origin: string,
): WorkspaceReference | null {
  try {
    const u = new URL(value, origin);
    if (u.origin !== origin || u.pathname !== "/workspace/reference")
      return null;
    const entityType = u.searchParams.get("type") ?? "",
      entityId = u.searchParams.get("id") ?? "";
    if (
      !Object.prototype.hasOwnProperty.call(referenceLabels, entityType) ||
      !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
        entityId,
      )
    )
      return null;
    return {
      entityType: entityType as WorkspaceReference["entityType"],
      entityId,
    };
  } catch {
    return null;
  }
}

export const referenceSearchHints: Record<
  WorkspaceReference["entityType"],
  string
> = {
  CANDIDATE: "Nom ou prénom",
  INSTRUCTOR: "Nom du moniteur ou du formateur",
  SECRETARY: "Nom ou prénom de la secrétaire",
  TASK: "Titre de la tâche",
  NOTE: "Titre de la note",
  MEETING: "Titre de la réunion",
  BOARD: "Titre du tableau",
  LESSON: "Date de la leçon (AAAA-MM-JJ)",
  EXAM: "Identifiant de l’examen",
  VEHICLE: "Immatriculation, marque ou modèle",
  INVOICE: "Numéro de facture ou d’avoir",
  PAYMENT: "Nom du candidat ou date (AAAA-MM-JJ)",
  DOCUMENT: "Libellé du document",
  RDVPERMIS_SLOT: "Identifiant du centre ou date (AAAA-MM-JJ)",
};

export const referenceGroups: {
  label: string;
  types: WorkspaceReference["entityType"][];
}[] = [
  { label: "PERSONNES", types: ["CANDIDATE", "INSTRUCTOR", "SECRETARY"] },
  { label: "COLLABORATION", types: ["TASK", "NOTE", "MEETING", "BOARD"] },
  { label: "PLANNING", types: ["LESSON", "EXAM", "RDVPERMIS_SLOT"] },
  { label: "GESTION", types: ["VEHICLE", "INVOICE", "PAYMENT", "DOCUMENT"] },
];

// ── Présentation des liens (refonte du 15/10/2026) ─────────────────────────────────────────────
// Libellés parlants selon l'objet (« Copier le lien du paiement ») et une couleur par famille, la même partout :
// personnes en indigo, planning en bleu ciel, gestion (factures, paiements, véhicules, documents) en vert,
// travail d'équipe (tâches, notes, réunions, tableaux) en ambre.
type RefType = WorkspaceReference["entityType"];
export type ReferenceTone = "people" | "planning" | "management" | "team";

export const referenceStyle: Record<RefType, { of: string; the: string; icon: LucideIcon; tone: ReferenceTone }> = {
  CANDIDATE: { of: "de l’élève", the: "l’élève", icon: GraduationCap, tone: "people" },
  INSTRUCTOR: { of: "du moniteur", the: "le moniteur", icon: UserCog, tone: "people" },
  SECRETARY: { of: "de la secrétaire", the: "la secrétaire", icon: User, tone: "people" },
  TASK: { of: "de la tâche", the: "la tâche", icon: ClipboardList, tone: "team" },
  NOTE: { of: "de la note", the: "la note", icon: NotebookPen, tone: "team" },
  MEETING: { of: "de la réunion", the: "la réunion", icon: Users, tone: "team" },
  BOARD: { of: "du tableau", the: "le tableau", icon: LayoutDashboard, tone: "team" },
  LESSON: { of: "de la leçon", the: "la leçon", icon: CalendarClock, tone: "planning" },
  EXAM: { of: "de l’examen", the: "l’examen", icon: GraduationCap, tone: "planning" },
  RDVPERMIS_SLOT: { of: "du créneau", the: "le créneau", icon: Calendar, tone: "planning" },
  VEHICLE: { of: "du véhicule", the: "le véhicule", icon: Car, tone: "management" },
  INVOICE: { of: "de la facture", the: "la facture", icon: Receipt, tone: "management" },
  PAYMENT: { of: "du paiement", the: "le paiement", icon: Wallet, tone: "management" },
  DOCUMENT: { of: "du document", the: "le document", icon: FileText, tone: "management" },
};

/** Classes Tailwind de chaque famille : pastille d'icône, cadre du bloc, texte d'accent. */
export const referenceToneClasses: Record<ReferenceTone, { chip: string; frame: string; text: string }> = {
  people: { chip: "bg-indigo-100 text-indigo-600", frame: "border-indigo-100 bg-indigo-50/60", text: "text-indigo-700" },
  planning: { chip: "bg-sky-100 text-sky-600", frame: "border-sky-100 bg-sky-50/60", text: "text-sky-700" },
  management: { chip: "bg-emerald-100 text-emerald-600", frame: "border-emerald-100 bg-emerald-50/60", text: "text-emerald-700" },
  team: { chip: "bg-amber-100 text-amber-600", frame: "border-amber-100 bg-amber-50/60", text: "text-amber-700" },
};
